import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "./api";
import { useLang } from "./i18n";

/**
 * Site-wide AI translation layer.
 * When a non-English language is selected, walks visible text nodes,
 * translates them via /api/translate (MongoDB + localStorage cached),
 * and swaps text in place. Switching back to English restores originals.
 */
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "INPUT", "SELECT", "OPTION", "CODE", "PRE", "SVG"]);
const MAX_BATCH = 120;

function loadCache(lang) {
  try {
    return JSON.parse(localStorage.getItem(`aj_tr_v2_${lang}`)) || {};
  } catch {
    return {};
  }
}

function saveCache(lang, dict) {
  try {
    const s = JSON.stringify(dict);
    if (s.length < 500000) localStorage.setItem(`aj_tr_v2_${lang}`, s);
  } catch {
    /* quota exceeded — in-memory cache still works */
  }
}

export default function AutoTranslate() {
  const { lang } = useLang();
  const location = useLocation();
  const originals = useRef(new Map()); // Text node -> original English string
  const phOriginals = useRef(new Map()); // Element -> original placeholder
  const cacheRef = useRef({}); // lang -> { english: translated }
  const applying = useRef(false);
  const runIdRef = useRef(0);

  useEffect(() => {
    const runId = ++runIdRef.current;
    const isEnglish = lang.startsWith("en");

    // Drop references to nodes React has removed
    for (const node of [...originals.current.keys()]) {
      if (!node.isConnected) originals.current.delete(node);
    }
    for (const el of [...phOriginals.current.keys()]) {
      if (!el.isConnected) phOriginals.current.delete(el);
    }

    const collectTextNodes = () => {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
          if (parent.closest("[data-no-translate]")) return NodeFilter.FILTER_REJECT;
          const text = originals.current.get(node) ?? node.nodeValue ?? "";
          const trimmed = text.trim();
          if (trimmed.length < 2) return NodeFilter.FILTER_REJECT;
          if (/^[\d\s\p{P}\p{S}]+$/u.test(trimmed)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      const nodes = [];
      let n;
      while ((n = walker.nextNode())) nodes.push(n);
      return nodes;
    };

    const collectPlaceholderEls = () =>
      Array.from(document.body.querySelectorAll("input[placeholder], textarea[placeholder]")).filter(
        (el) => !el.closest("[data-no-translate]")
      );

    const guardApply = (fn) => {
      applying.current = true;
      fn();
      setTimeout(() => {
        applying.current = false;
      }, 0);
    };

    const restore = () =>
      guardApply(() => {
        originals.current.forEach((orig, node) => {
          if (node.isConnected && node.nodeValue !== orig) node.nodeValue = orig;
        });
        phOriginals.current.forEach((orig, el) => {
          if (el.isConnected) el.setAttribute("placeholder", orig);
        });
      });

    const apply = (nodes, phEls, dict) =>
      guardApply(() => {
        for (const node of nodes) {
          const orig = originals.current.get(node) ?? node.nodeValue;
          originals.current.set(node, orig);
          const m = orig.match(/^(\s*)([\s\S]*?)(\s*)$/);
          const t = dict[m[2]];
          if (t && node.isConnected) node.nodeValue = m[1] + t + m[3];
        }
        for (const el of phEls) {
          const orig = phOriginals.current.get(el) ?? el.getAttribute("placeholder");
          phOriginals.current.set(el, orig);
          const t = dict[(orig || "").trim()];
          if (t && el.isConnected) el.setAttribute("placeholder", t);
        }
      });

    const run = async () => {
      if (isEnglish) {
        restore();
        return;
      }
      const nodes = collectTextNodes();
      const phEls = collectPlaceholderEls();

      const texts = new Set();
      for (const node of nodes) {
        const orig = originals.current.get(node) ?? node.nodeValue;
        originals.current.set(node, orig);
        texts.add(orig.trim());
      }
      for (const el of phEls) {
        const orig = phOriginals.current.get(el) ?? el.getAttribute("placeholder");
        phOriginals.current.set(el, orig);
        if (orig && orig.trim().length > 1) texts.add(orig.trim());
      }

      if (!cacheRef.current[lang]) cacheRef.current[lang] = loadCache(lang);
      const dict = cacheRef.current[lang];
      const missing = [...texts].filter((t) => !dict[t]);

      apply(nodes, phEls, dict); // apply cached translations instantly

      if (missing.length) {
        document.documentElement.setAttribute("data-translating", "true");
        try {
          for (let i = 0; i < missing.length; i += MAX_BATCH) {
            const { data } = await api.post("/translate", { lang, texts: missing.slice(i, i + MAX_BATCH) });
            if (runIdRef.current !== runId) return;
            Object.assign(dict, data.translations || {});
            apply(collectTextNodes(), collectPlaceholderEls(), dict);
          }
          saveCache(lang, dict);
        } catch (e) {
          console.warn("Translation request failed:", e?.message);
        } finally {
          document.documentElement.removeAttribute("data-translating");
        }
      }
    };

    run();

    let timer;
    const observer = new MutationObserver(() => {
      if (applying.current) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (runIdRef.current === runId) run();
      }, 500);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [lang, location.pathname]);

  return null;
}
